/**
 * `/accesso-ofc`: entry of the Argo access (PE-2215).
 *
 * Argo opens `/accesso-ofc#ticket=…` with a one-time ticket valid for a minute.
 * The ticket sits in the fragment, so it reaches neither the CloudFront logs
 * nor the Referer, and it is the only credential of the redeem call: the
 * browser exchanges it directly with Argo for a Parse session of the
 * organization's service account.
 */
import { resetUserSession } from '~src/user';
import { deferParseSessionRevocation, storeParseSession } from './parseSession';
import { getArgoUrl, isArgoExitUrl, storeOfcAccess } from './ofcAccess';
import { OFC_ACCESS_PAGES, renderOfcAccessPage } from './ofcAccessPage';

const TICKET_PATTERN = /^[A-Za-z0-9_-]{43}$/;
const SESSION_TOKEN_PATTERN = /^r:[0-9a-f]{32}$/;
const REDEEM_TIMEOUT_MS = 15000;
const AUTH0_CACHE_PREFIX = '@@auth0spajs@@';

export const REDEEM_STATUS = Object.freeze({
  OK: 'OK',
  TICKET_INVALID: 'TICKET_INVALID',
  UNAVAILABLE: 'UNAVAILABLE',
});

/**
 * Reads the ticket and immediately removes it from the address bar and the
 * history entry, whatever its value: it must not survive a reload, a bookmark
 * or a shared screenshot.
 */
export const takeTicketFromLocation = () => {
  const { hash, pathname, search } = window.location;
  const ticket = new URLSearchParams(hash.replace(/^#/, '')).get('ticket');

  window.history.replaceState(window.history.state, '', pathname + search);

  return ticket && TICKET_PATTERN.test(ticket) ? ticket : null;
};

const textOrNull = (value) =>
  typeof value === 'string' && value.length > 0 ? value : null;

// The response becomes a credential and a navigation target: anything
// unexpected is refused as a whole.
export const parseRedeemResponse = (data, now = Date.now()) => {
  if (!data || typeof data !== 'object') return null;

  const {
    accessId,
    sessionToken,
    expiresAt,
    userId,
    orgId,
    operator,
    exitUrl,
  } = data;
  const expiry = typeof expiresAt === 'string' ? Date.parse(expiresAt) : NaN;

  if (
    !textOrNull(accessId) ||
    typeof sessionToken !== 'string' ||
    !SESSION_TOKEN_PATTERN.test(sessionToken) ||
    !Number.isFinite(expiry) ||
    expiry <= now ||
    !isArgoExitUrl(exitUrl)
  ) {
    return null;
  }

  return {
    accessId,
    sessionToken,
    expiresAt,
    userId: textOrNull(userId),
    orgId: textOrNull(orgId),
    exitUrl,
    operator: {
      name: textOrNull(operator?.name) || '',
      email: textOrNull(operator?.email),
    },
  };
};

export const redeemOfcTicket = async (ticket) => {
  const argoUrl = getArgoUrl();
  if (!argoUrl) return { status: REDEEM_STATUS.UNAVAILABLE };

  const controller =
    typeof AbortController === 'function' ? new AbortController() : null;
  const timeout = setTimeout(() => controller?.abort(), REDEEM_TIMEOUT_MS);

  try {
    const response = await fetch(`${argoUrl}/api/public/giotto-access/redeem`, {
      method: 'POST',
      // The ticket is the only credential: no Argo cookies.
      credentials: 'omit',
      cache: 'no-store',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ticket }),
      signal: controller?.signal,
    });

    if (response.status === 410) {
      return { status: REDEEM_STATUS.TICKET_INVALID };
    }
    if (!response.ok) {
      return { status: REDEEM_STATUS.UNAVAILABLE };
    }

    const access = parseRedeemResponse(await response.json());
    return access
      ? { status: REDEEM_STATUS.OK, access }
      : { status: REDEEM_STATUS.UNAVAILABLE };
  } catch (error) {
    return { status: REDEEM_STATUS.UNAVAILABLE };
  } finally {
    clearTimeout(timeout);
  }
};

const clearAuth0Cache = () => {
  Object.keys(localStorage)
    .filter((key) => key.startsWith(AUTH0_CACHE_PREFIX))
    .forEach((key) => localStorage.removeItem(key));
};

/**
 * Replaces whatever identity the browser had with the service account: the
 * previous Parse session is revoked (or handed to the next logout when Parse
 * is not reachable), the user context, the feature caches and the Auth0 token
 * cache are dropped, then the new session and the access are stored.
 */
export const startOfcAccess = async (access) => {
  try {
    await resetUserSession({ revoke: true });
  } catch (error) {
    deferParseSessionRevocation();
  }

  clearAuth0Cache();
  localStorage.removeItem('authRedirect');

  storeParseSession(access.sessionToken);
  storeOfcAccess(access);
};

export const handleOfcAccess = async () => {
  const ticket = takeTicketFromLocation();
  const argoUrl = getArgoUrl();

  if (!ticket) {
    renderOfcAccessPage(OFC_ACCESS_PAGES.TICKET_INVALID, argoUrl);
    return;
  }

  const result = await redeemOfcTicket(ticket);

  if (result.status === REDEEM_STATUS.TICKET_INVALID) {
    renderOfcAccessPage(OFC_ACCESS_PAGES.TICKET_INVALID, argoUrl);
    return;
  }
  if (result.status !== REDEEM_STATUS.OK) {
    renderOfcAccessPage(OFC_ACCESS_PAGES.UNAVAILABLE, argoUrl);
    return;
  }

  await startOfcAccess(result.access);

  // A full load starts from clean in-memory state, and `replace` keeps
  // /accesso-ofc out of the history.
  window.location.replace(
    `${window.location.origin}${process.env.ROUTE_START}`
  );
};
